import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useParams, useRouteMatch, useHistory, useLocation } from "react-router-dom";

import { actionGroup, switchInteractivePageSection } from "actions/editor";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";

import _ from "lodash";
import cx from "classnames";

import PageDetailCard from "./pageDetailsCard";
import { HeaderContext } from "pages/pages/components/header";
import StoryboardContext from "components/Storyboard/context";
import { EditorContext } from "components/Editor";
import { hasUnsavedChanges } from "pages/pages/helper/index";
import { SaveDialog } from "components/Modal";
import { createPage, rearrangePageWidgets, searchPages, updatePage } from "actions/pages";
import { getBaseUrl } from "services/util";
import { BackgroundView } from "../../background";
import { colorToString } from "components/Editor/helper/color";
import { Property } from "components/Editor/components/properties";
import { Button, Accordion } from "react-bootstrap";

import styles from "./components.module.scss";

export const SlidePageDetailsComponent = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const listReference = React.createRef(null);
  const headerContext = React.useContext(HeaderContext);
  const history = useHistory();
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const { id } = useParams();
  const background = useSelector((state) =>
    _.get(state, "storyboards.storyboard.item.appearance.background")
  );
  const loading = useSelector(state => _.get(state, 'pages.loading', false));
  const fetched = useSelector(state => _.get(state, 'pages.fetched', false));
  const pagesList = useSelector((state) => _.get(state, `pages.items`, []));
  const creating = useSelector(state => _.get(state, 'pages.creating', false));

  const [items, setItems] = useState(pagesList);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const slideId = queryParams.get("slide") || "";

  useEffect(() => {
    if (!_.isEqual(items, pagesList)) {
      setItems(pagesList);
    }
  }, [pagesList]);

  const storyboardContext = React.useContext(StoryboardContext);
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext?.editorSession?.id;

  useEffect(() => {
    if(slideId && editorContext?.editorSession?.id) {
      dispatch(switchInteractivePageSection(editorContext.editorSession?.id, slideId));
    }
  }, [slideId, editorContext?.editorSession?.id]);


  const handleDragEnd = (event) => {
    const selecto = editorContext.editorSession.selecto.current;
    const selectoElement = document.querySelector(`.${selecto.selectionElement.classList[0]}`);
    selectoElement.setAttribute("style", "display: none;");

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => `draggable-${item.id}` === active.id);
      const newIndex = items.findIndex((item) => `draggable-${item.id}` === over.id);
      const orderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({ ...item, order: index }));
      setItems(orderedItems);

      const newItems = orderedItems.map(item => _.pick(item, ["id", "order"]));
      let request = {
        entityOrderList: newItems
      }
      dispatch(rearrangePageWidgets(id, request, orderedItems));
    }
  };

  var {
  currentWidgets,
  previousWidgets,
  menuDesigns,
  previousMenuDesigns,
  variableMappings,
  deletedVariableMappings,
  updateState,
  updating,
  entityType,
  } = useSelector(
    (state) => ({
      currentWidgets: _.get(state, `${editorSession}.present.widgets`, []),
      previousWidgets: _.get(state, `pages.page.${id}.item.draft.widgets`, []),
      menuDesigns: _.get(state, `${editorSession}.present.appearance`),
      previousMenuDesigns: _.get(state, `pages.page.${id}.item.storyboard.appearance`),
      variableMappings: _.get(state, `pages.page.${id}.item.variableMappings`, []),
      deletedVariableMappings: _.get(state, `pages.page.${id}.item.deletedVariableMappings`, []),
      updateState: _.get(state, `pages.page.${id}.update`),
      updating: _.get(state, `pages.page.${id}.update.updating`, false),
      entityType: _.get(state, `${editorSession}.present.entityType`),
    }),
    shallowEqual
  );

  const storyboardType = storyboardContext?.type;

  const hasChanges = hasUnsavedChanges({
    currentWidgets,
    previousWidgets,
    menuDesigns,
    previousMenuDesigns,
    variableMappings,
    deletedVariableMappings,
  });

  const saveError = updateState?.error;
  const saveSucceeded = updateState?.success === true;
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [waitingForSave, setWaitingForSave] = useState(false);
  const pendingNavigationRef = React.useRef(null);

  const handlePageSave = () => {
    headerContext.onSave();
  };

  useEffect(() => {
    if (!waitingForSave) {
      return;
    }

    if (updating) {
      return;
    }

    if (saveError) {
      setWaitingForSave(false);
      return;
    }

    if (!saveSucceeded && hasChanges) {
      return;
    }

    setWaitingForSave(false);
    setShowSaveModal(false);

    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  }, [waitingForSave, updating, hasChanges, saveError, saveSucceeded]);

  const handleAddPageClick = () => {
    if (!creating) {
      var request = {
        name: `Page ${_.size(pagesList) + 1}`,
        storyboard: storyboardContext.id,
        order: _.last(pagesList).order + 1
      };

      const proceedPageCreation = () => {
        actionGroup.start();
        // dispatch(updatePage(page.id, editorSession.current));
        dispatch(
          createPage(request, (response) => {
            history.push(`${baseUrl}/pages/${response.id}`);
          })
        );
        actionGroup.end();
      };

      if (hasChanges && storyboardType === "SPATIAL" && entityType === "PAGE") {
        pendingNavigationRef.current = proceedPageCreation;
        setShowSaveModal(true);
        return;
      }

      proceedPageCreation();
    }
  };

  useEffect(() => {
    if (storyboardContext && !fetched) {
      handleSearchPages();
    }
  }, [storyboardContext]);

  const handleSearchPages = () => {
    var request = {
      storyboard: storyboardContext.id
    }

    dispatch(searchPages(request, 0, 50));
  }

  return (
    <div
      className={cx(styles.pageDetailsContainer, {
        [styles.showPageDetails]: show,
      })}
    >
      <div className={styles.overlay} />
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <span className={styles.title}>Pages & Slides</span>
            <Button
              variant="info"
              onClick={onClose}
              className={styles.closeButton}
            >
              <i className={cx(styles.icon, "cleverstory-close")}></i>
            </Button>
          </div>
          <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((widget) => `draggable-${widget.id}`)}>
              <div className={styles.actionContainer}>
                <div className={styles.inner} ref={listReference}>
                  <Accordion className={styles.accordWrapper} defaultActiveKey={id}>
                    {_.map(items, (page) => {
                      return (
                        <PageDetailCard
                          key={page.id}
                          id={`draggable-${page.id}`}
                          eventKey={page.id}
                          page={page}
                          currentPage={id}
                        />
                      );
                    })}
                  </Accordion>
               </div>
              </div>
            </SortableContext>
          </DndContext>
          <div className={styles.addPageButtonContainer}>
            <Button
              onClick={handleAddPageClick}
              className={styles.addPageButton}
              variant="primary"
            >
              <i className={cx("cleverstory-plus", styles.icon)} />
              Add Page
            </Button>
          </div>
        </div>
        {showSaveModal && (
          <div onClick={(e) => e.stopPropagation()}>
            <SaveDialog
              isSaving={waitingForSave || updating} 
              error={saveError}
              onCancel={() => {
                setShowSaveModal(false);
                pendingNavigationRef.current = null;
              }}
              onConfirm={() => {
                setWaitingForSave(true);
                handlePageSave();
              }}
            />
          </div>
        )}
      </div>
  );
};

const FLIPBOOK_DARK = { r: 98, g: 98, b: 98, a: 1 };
const FLIPBOOK_LIGHT = { r: 238, g: 238, b: 238, a: 1 };
const GLASS_DARK = { r: 98, g: 98, b: 98, a: 0.25 };
const GLASS_LIGHT = { r: 238, g: 238, b: 238, a: 0.25 };

/** Flip-book color fills: same shape as page/widget `BackgroundType` — `type: color`, nested `color.type: solid`, no `blur`. */
function flipbookColorBackgroundPayload(background, colorInput) {
  const r = colorInput.r ?? 238;
  const g = colorInput.g ?? 238;
  const b = colorInput.b ?? 238;
  return {
    ..._.omit(background, ['blur']),
    type: 'color',
    color: {
      r,
      g,
      b,
      a: 1,
      type: 'solid',
    },
    media: null,
  };
}

export const GlassThemesSettings = React.memo(
  ({ background, handleBackgroundChange, handleCustomBlur, isFlipBook = false }) => {
    const defaults = isFlipBook
      ? {
          type: 'color',
          color: { ...FLIPBOOK_LIGHT, type: 'solid' },
        }
      : { type: 'glass', color: GLASS_LIGHT, blur: 25 };
    const { type, color, blur } = background || defaults;

    const [custom, setCustom] = useState(false);

    const fillType = isFlipBook ? 'color' : 'glass';
    const darkPreset = isFlipBook ? FLIPBOOK_DARK : GLASS_DARK;
    const lightPreset = isFlipBook ? FLIPBOOK_LIGHT : GLASS_LIGHT;

    const handleChangeBackgroundColor = (nextColor) => {
      if (isFlipBook) {
        handleBackgroundChange(
          flipbookColorBackgroundPayload(background, {
            ...nextColor,
            a: 1,
          })
        );
        return;
      }
      handleBackgroundChange({
        ...background,
        type: 'glass',
        blur: 25,
        color: {
          ...nextColor,
          a: 0.25,
        },
        media: null,
      });
    };

    useEffect(() => {
      if (!isFlipBook || !background) return;
      const c = background.color || {};
      const a = c.a === undefined ? 1 : Number(c.a);
      const blurVal = Number(background.blur) || 0;
      const isPageStyleColorFill =
        background.type === 'color' &&
        c.type === 'solid' &&
        a >= 1 &&
        blurVal === 0;
      if (!isPageStyleColorFill) {
        handleBackgroundChange(
          flipbookColorBackgroundPayload(background, {
            r: c.r ?? 238,
            g: c.g ?? 238,
            b: c.b ?? 238,
            a: 1,
          })
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- parent handler identity is not stable
    }, [isFlipBook, background]);

    useEffect(() => {
      if (!_.isEqual(_.omit(color, ['type']), darkPreset) && !_.isEqual(_.omit(color, ['type']), lightPreset)) {
        setCustom(true);
      }

      return () => {
        setCustom(false);
      };
    }, [background, color, darkPreset, lightPreset]);

    const isFillActive =
      type === fillType ||
      (isFlipBook && (type === 'glass' || type === 'solid'));

    return (
      <>
        <Property name={isFlipBook ? 'Solid fill' : 'Glass Effect'}>
          <div className={styles.glassThemeContainer}>
            <div
              className={styles.themeButton}
              onClick={() => {
                handleChangeBackgroundColor(darkPreset);
                setCustom(false);
              }}
            >
              <div
                className={cx(styles.darkBox, {
                  [styles.active]:
                    isFillActive &&
                    _.isEqual(_.omit(color, ['type']), darkPreset),
                })}
              >
                <span className={styles.swatch} />
              </div>
              <span className={styles.title}>Dark</span>
            </div>
            <div
              className={styles.themeButton}
              onClick={() => {
                handleChangeBackgroundColor(lightPreset);
                setCustom(false);
              }}
            >
              <div
                className={cx(styles.lightBox, {
                  [styles.active]:
                    isFillActive &&
                    _.isEqual(_.omit(color, ['type']), lightPreset),
                })}
              >
                <span className={styles.swatch} />
              </div>
              <span className={styles.title}>Light</span>
            </div>
            <Property.ColorButton
              trigger={
                <div className={styles.themeButton}>
                  <div
                    className={cx(styles.customBox, {
                      [styles.active]:
                        isFillActive &&
                        !_.isEqual(_.omit(color, ['type']), darkPreset) &&
                        !_.isEqual(_.omit(color, ['type']), lightPreset),
                    })}
                  >
                    <span
                      style={{
                        backgroundColor:
                          isFillActive &&
                          !_.isEqual(_.omit(color, ['type']), darkPreset) &&
                          !_.isEqual(_.omit(color, ['type']), lightPreset)
                            ? colorToString({ ...color, a: isFlipBook ? 1 : color?.a })
                            : "#FFC600",
                      }}
                      className={styles.swatch}
                    />
                  </div>
                  <span className={styles.title}>Custom</span>
                </div>
              }
              value={
                isFlipBook
                  ? { ...color, a: 1, type: color?.type || 'solid' }
                  : color
              }
              onChange={(next) => {
                handleChangeBackgroundColor({
                  ...next,
                  a: isFlipBook ? 1 : next.a,
                  ...(isFlipBook ? { type: 'solid' } : {}),
                });
              }}
            />
          </div>
        </Property>
        {custom && handleCustomBlur && (
          <Property name="Background Blur">
            <Property.Slider
              input
              min={5}
              className={styles.customSlider}
              max={100}
              step={1}
              value={blur || 0}
              onChange={handleCustomBlur}
            />
          </Property>
        )}
      </>
    );
  }
);
